import React, {
	forwardRef,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	type ForwardedRef,
} from 'react';
import { JiraEntryPointContainer } from '@atlassian/jira-entry-point-container/src/index.tsx';
import type {
	IssueCandidate,
	BacklogAiWorkCreationPanelProps,
} from '@atlassian/jira-software-backlog-ai-work-creation/src/view/ai-panel/types.tsx';
import { GIC_LIFECYCLE_EVENTS } from '@atlassian/jira-issue-create-extensibility/src/common/utils/lifecycle-events/constants.tsx';
import { useGICLifeCycleEvents } from '@atlassian/jira-issue-create-extensibility/src/common/utils/lifecycle-events/main.tsx';
import { useAnalyticsEvents, fireTrackAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { useBacklogSelector, useBacklogDispatch } from '../../../state/index.tsx';
import { getProjectKey, projectIdSelector } from '../../../state/selectors/app/index.tsx';
import type { AiWorkCreatePanelEntryPointSubject } from '../inline-card-create/ai-work-create/types.tsx';
import { openModal } from '../../../state/actions/modals/index.tsx';
import { GlobalIssueCreateType } from '../../../model/modals/types.tsx';
import { getFilters } from '../../../state/selectors/filters/index.tsx';
import { cardCreateMultipleRequest } from '../../../state/actions/cards/create/index.tsx';
import { getLastAppliedParentFilters } from '../../../state/selectors/card-create/index.tsx';
import { useStoreFields } from '../../../common/hooks/use-store-fields/main.tsx';
import { issueParentsSelector } from '../../../state/selectors/issue-parent/index.tsx';
import { getInlineCreateSessionId } from '../../../state/selectors/inline-create/index.tsx';
import { getBaseLevelCardTypes } from '../../../state/selectors/card-type/index.tsx';
import { getCardDescriptionAsADF } from '../../../common/utils.tsx';

type Props = {
	subject: AiWorkCreatePanelEntryPointSubject;
};

// **Following is copied from view/card-list/inline-card-create/index.tsx**
// Hacky way to generate optimistic issue id.
// Not ideal, but have to do it for now to react backwards compatibility with current ICC.
let lastCardId = 0;
const generateId = (): number => {
	lastCardId -= 1;
	return lastCardId;
};

export const AiWorkCreatePanel = forwardRef(
	({ subject }: Props, ref: ForwardedRef<HTMLDivElement>) => {
		const projectKey = useBacklogSelector(getProjectKey);
		const projectId = useBacklogSelector(projectIdSelector);

		const baseLevelCardTypes = useBacklogSelector(getBaseLevelCardTypes).map(
			({ id, hasRequiredFields, ...rest }) => ({
				...rest,
				id: String(id),
				hasRequiredField: Boolean(hasRequiredFields),
			}),
		);
		const parentId = useBacklogSelector(getLastAppliedParentFilters);
		const enabledFilters = useBacklogSelector(getFilters);
		const issueParents = useBacklogSelector((state) => issueParentsSelector(state));
		const dispatch = useBacklogDispatch();
		const onAcceptGic = useRef<null | (() => void)>();
		const { subscribe } = useGICLifeCycleEvents();

		const { storeFieldsFromCardCreateData, storeFieldsFromFetchIssuesData } = useStoreFields({
			issueParents,
		});
		const { createAnalyticsEvent } = useAnalyticsEvents();

		const inlineCreateSessionId = useBacklogSelector(getInlineCreateSessionId);

		const onAccept = useCallback<BacklogAiWorkCreationPanelProps['onAccept']>(
			(issues) => {
				const { issueTypeId } = issues[0];
				const cardTypeEntity = baseLevelCardTypes.find((ct) => ct.id === issueTypeId);
				const fixVersions = enabledFilters.VERSION || [];
				const tempIds = issues.map(() => generateId());

				/** Analytics: We indicate to the analytics for issue creation that the inline creation ended up finishing inline creation (With session ID) */
				const analyticsEvent = createAnalyticsEvent({
					action: 'created',
					actionSubject: 'issue',
					sessionId: inlineCreateSessionId,
					aiFeatureUsed: 'ai-work-creation-from-link',
				});
				dispatch(
					cardCreateMultipleRequest({
						tempIds,
						summaries: issues.map((inlineCard) => inlineCard.summary),
						descriptions: issues.map((inlineCard) => inlineCard.description),
						typeId: Number(issueTypeId),
						typeIcon: cardTypeEntity ? cardTypeEntity.iconUrl : '',
						parentId,
						fixVersions,
						insertBefore: null,
						listId: 'BACKLOG',
						storeFieldsFromCardCreateData,
						storeFieldsFromFetchIssuesData,
						analyticsEvent,
					}),
				);
			},
			[
				dispatch,
				baseLevelCardTypes,
				enabledFilters,
				parentId,
				storeFieldsFromCardCreateData,
				storeFieldsFromFetchIssuesData,
				createAnalyticsEvent,
				inlineCreateSessionId,
			],
		);

		const onEdit = useCallback(
			(
				{ summary, description, issueTypeId }: IssueCandidate,
				{
					onComplete,
				}: {
					onComplete: () => void;
				},
			) => {
				const fixVersions = enabledFilters.VERSION || [];
				// should never be a string, off chance that it might be we transform it
				const cardDescription = getCardDescriptionAsADF(description);

				dispatch(
					openModal({
						type: GlobalIssueCreateType,
						payload: {
							cardListId: 'BACKLOG',
							cardParentId: parentId,
							cardSummary: summary,
							cardTypeId: parseInt(issueTypeId, 10),
							cardDescription,
							rankBeforeCardId: null,
							fixVersions,
						},
					}),
				);

				onAcceptGic.current = onComplete;
			},
			[dispatch, enabledFilters, parentId],
		);

		useEffect(
			() =>
				subscribe?.(GIC_LIFECYCLE_EVENTS.MODAL_CLOSED, async ({ createdIssues }) => {
					// prevent this from being called if GIC is opened elsewhere (e.g. create button next to nav bar)
					if (onAcceptGic.current && createdIssues.length) {
						const analyticsEvent = createAnalyticsEvent({});
						fireTrackAnalytics(analyticsEvent, 'aiResult actioned', 'suggestedWorkItemGicCreated', {
							aiResultAction: 'recommendationAccepted',
							acceptedNumber: 1,
							aiFeatureName: 'backlog-ai-work-creation',
							isAIFeature: 1,
							proactiveAIGenerated: 0,
							userGeneratedAI: 1,
							userLocation: 'backlog',
							createdIssueId: createdIssues[0]?.issueId,
						});

						onAcceptGic.current?.();
					}
					onAcceptGic.current = null;
				}),
			[createAnalyticsEvent, subscribe],
		);

		const runtimeProps = useMemo<BacklogAiWorkCreationPanelProps>(
			() => ({
				onAccept,
				onEdit,
				projectKey,
				projectId,
			}),
			[projectKey, onEdit, onAccept, projectId],
		);

		return (
			<div ref={ref}>
				<JiraEntryPointContainer
					entryPointReferenceSubject={subject}
					id="aiWorkCreatePanel"
					packageName="backlog"
					errorFallback="unmount"
					teamName="Crocs"
					runtimeProps={runtimeProps}
				/>
			</div>
		);
	},
);
